/**
 * The session token issued at login.
 *
 * Most of the portal's endpoints identify the caller by a user id in the request,
 * but anything holding private data — currently the File Drive — needs the server
 * to verify who is asking. Login hands back a token for that, and it is kept here
 * so API modules can read it without reaching into React context.
 */

const SESSION_KEY = 'techforce_session_token';

/** Where AuthContext keeps the signed-in user. Exported so there is one definition. */
export const AUTH_USER_STORAGE_KEY = 'techforce_user';

export function getSessionToken(): string | null {
  try {
    const token = localStorage.getItem(SESSION_KEY);
    return token && token.trim() ? token : null;
  } catch {
    return null;
  }
}

export function setSessionToken(token: string | null): void {
  try {
    if (token && token.trim()) localStorage.setItem(SESSION_KEY, token);
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    // private browsing with storage disabled: the drive will ask them to sign in again
  }
}

export function authHeaders(): Record<string, string> {
  const token = getSessionToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** The signed-in user's id, for endpoints that record who made a change. */
export function getStoredUserId(): number | null {
  try {
    const raw = localStorage.getItem(AUTH_USER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: unknown };
    return typeof parsed?.id === 'number' && Number.isInteger(parsed.id) ? parsed.id : null;
  } catch {
    return null;
  }
}

/**
 * Headers for a JSON write that should be attributed to the caller.
 *
 * The token is the only identity the server can verify; the user id sent alongside
 * in the body covers logins issued before sessions existed. Neither is required —
 * an unattributed change still saves, it just shows up in the inventory log
 * without a name.
 */
export function actorJsonHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', ...authHeaders() };
}

/** Adds the fallback user id to a request body. See actorJsonHeaders. */
export function withActor<T extends object>(body: T): T & { actorUserId: number | null } {
  return { ...body, actorUserId: getStoredUserId() };
}

/** Thrown when the server rejects the token, so pages can prompt a fresh sign-in. */
export class SessionExpiredError extends Error {
  constructor(message = 'Please sign in again to continue.') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

/** Turns a failed response into the right error, so a 401 is distinguishable. */
export async function throwApiError(res: Response, fallback: string): Promise<never> {
  let message = fallback;
  try {
    const body = await res.json();
    if (body?.error) message = body.error as string;
    if (res.status === 401 || body?.code === 'SESSION_REQUIRED') throw new SessionExpiredError(message);
  } catch (err) {
    if (err instanceof SessionExpiredError) throw err;
  }
  if (res.status === 401) throw new SessionExpiredError();
  throw new Error(message);
}
